import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Card,
  Row,
  Col,
  message,
  Space,
  Typography,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { categoryService } from '../../services';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const CategoryForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  // imageList removed: images are not stored in DB schema currently
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    loadCategories();
    if (id) {
      setIsEditing(true);
      loadCategory(id);
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const response = await categoryService.getAll();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      //console.error('Error loading categories:', error);
    }
  };

  const loadCategory = async (categoryId) => {
    setLoading(true);
    try {
      const response = await categoryService.getById(categoryId);
      if (response.success) {
  const categoryData = response.data || {};
  // normalize possible field names returned by API
  categoryData.parent_id = categoryData.parent_id ?? categoryData.parentId ?? null;
  // ensure id field exists as category_id for consistency
  if (categoryData.id && !categoryData.category_id) categoryData.category_id = categoryData.id;
  form.setFieldsValue(categoryData);
        
  // No image field in DB schema: nothing to load for image
      } else {
        message.error('Không thể tải thông tin phân loại');
      }
    } catch (error) {
      //console.error('Error loading category:', error);
      message.error('Không thể tải thông tin phân loại');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      message.error('Bạn chưa đăng nhập hoặc token không hợp lệ. Vui lòng đăng nhập lại.');
      return;
    }
    setLoading(true);
    try {
      // Only send fields that exist in the database schema (id, name, description, parent_id)
      const categoryData = {
        name: values.name,
  description: values.description || null,
  parent_id: values.parent_id ? Number(values.parent_id) : null,
      };

      let response;
      if (isEditing) {
        response = await categoryService.update(id, categoryData);
      } else {
        response = await categoryService.create(categoryData);
      }

      if (response.success) {
        message.success(
          isEditing ? 'Cập nhật phân loại thành công' : 'Thêm phân loại thành công'
        );
        navigate('/categories/list');
      } else {
        message.error(response.message || 'Lưu phân loại thất bại');
      }
    } catch (error) {
      //console.error('Error saving category:', error);
      message.error('Lưu phân loại thất bại');
    } finally {
      setLoading(false);
    }
  };

  // image upload handlers removed because DB does not store images

  // Prepare flat options for parent select (exclude current editing id)
  const excludeId = id ? Number(id) : null;
    const parentOptions = categories
      .map(cat => ({
        id: cat.category_id ?? cat.id ?? null,
        name: cat.name,
        parent_id: cat.parent_id ?? cat.parentId ?? null,
      }))
      .filter(cat => cat.id !== null && cat.id !== excludeId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(cat => (
        <Select.Option key={cat.id} value={cat.id} label={`${cat.id} - ${cat.name}`}>
          {`${cat.id} - ${cat.name}`}
        </Select.Option>
      ));

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/categories/list')}
            >
              Quay lại
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {isEditing ? 'Chỉnh sửa phân loại' : 'Thêm phân loại mới'}
            </Title>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
              parent_id: null,
            }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24}>
              <Card title="Thông tin phân loại" size="small">
                <Row gutter={[16, 16]}>
                  <Col xs={24}>
                    <Form.Item
                      name="name"
                      label="Tên phân loại"
                      rules={[
                        { required: true, message: 'Vui lòng nhập tên phân loại' },
                        { max: 255, message: 'Tên phân loại không được quá 255 ký tự' },
                      ]}
                    >
                      <Input placeholder="Nhập tên phân loại" />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      name="description"
                      label="Mô tả"
                    >
                      <TextArea 
                        rows={4} 
                        placeholder="Nhập mô tả về phân loại này" 
                        maxLength={1000}
                        showCount
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="parent_id"
                      label="Phân loại cha"
                    >
                      <Select
                        showSearch
                        allowClear
                        placeholder="Chọn phân loại cha (tùy chọn)"
                        optionFilterProp="children"
                        optionLabelProp="children"
                        filterOption={(input, option) => {
                          const text = (option.children || '').toString();
                          return text.toLowerCase().includes(input.toLowerCase());
                        }}
                      >
                        {parentOptions}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <div style={{ textAlign: 'right', marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 24 }}>
            <Space>
              <Button onClick={() => navigate('/categories/list')}>
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
              >
                {isEditing ? 'Cập nhật phân loại' : 'Tạo phân loại'}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CategoryForm;
